import React, { useEffect } from 'react';
import { formatMoney } from '../../utils/formatMoney';
import { Sparkles, Plus } from 'lucide-react';
import type { Product } from '../../types/order';
import { productDisplayPrice } from '../../utils/pricing';

interface Props {
  /** Το μήνυμα των κεντρικών, όπως το λέει ο ταμίας στον πελάτη. */
  message: string;
  products: Product[];
  channel: 'take_away' | 'delivery';
  onAdd: (product: Product) => void;
  /** «Όχι, ευχαριστώ» — και Esc ή πάτημα έξω από το παράθυρο. */
  onDecline: () => void;
}

/**
 * Πρόταση πώλησης πριν από την πληρωμή (κεντρικά → «Προτάσεις πώλησης»). Εμφανίζεται
 * μία φορά ανά παραγγελία στο «Ολοκλήρωση»/«Checkout». Το πάτημα σε προϊόν το προσθέτει
 * ακριβώς όπως ο κατάλογος (μεγέθη/υποχρεωτικές επιλογές ανοίγουν τα γνωστά παράθυρα),
 * με την κανονική τιμή· μετά — ή με «Όχι, ευχαριστώ» — το ταμείο συνεχίζει στην πληρωμή.
 */
const UpsellPrompt: React.FC<Props> = ({ message, products, channel, onAdd, onDecline }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDecline(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDecline]);

  const cols = products.length === 1 ? 'grid-cols-1 max-w-xs mx-auto'
    : products.length === 2 || products.length === 4 ? 'grid-cols-2'
    : 'grid-cols-2 sm:grid-cols-3';

  return (
    <div
      className="cashier-overlay fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
      onClick={onDecline}
    >
      <div
        className="cashier-dialog bg-surface-0 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Πρόταση πώλησης"
        data-testid="upsell-prompt"
      >
        <div className="px-6 pt-6 pb-4 text-center shrink-0">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-amber-500/15 flex items-center justify-center mb-3">
            <Sparkles className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-t1 leading-snug">{message}</p>
        </div>

        <div className="px-6 pb-4 overflow-y-auto">
          <div className={`grid gap-3 ${cols}`}>
            {products.map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => onAdd(p)}
                data-testid={`upsell-add-${p.id}`}
                className="group min-h-[120px] rounded-2xl border-2 border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 active:scale-[0.98] transition-all touch-manipulation flex flex-col items-center justify-center gap-2 p-4 text-center"
              >
                {p.image_url && (
                  <img src={p.image_url} alt="" className="h-14 w-14 object-cover rounded-xl" />
                )}
                <span className="text-base font-semibold text-t1 leading-tight">{p.name}</span>
                <span className="flex items-center gap-1.5 text-sm font-bold text-amber-700 dark:text-amber-300">
                  <Plus className="h-4 w-4" />
                  {formatMoney(productDisplayPrice(p, channel))}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-line bg-surface-1 shrink-0">
          <button
            type="button"
            onClick={onDecline}
            data-testid="upsell-decline"
            className="w-full h-14 rounded-xl bg-surface-2 hover:bg-surface-3 text-t1 text-lg font-semibold active:scale-[0.99] transition-all touch-manipulation"
          >
            Όχι, ευχαριστώ
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpsellPrompt;
